import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";

export default function BarChart({
  items,
  dispatch,
  questionState,
  isExplanation = false,
  presetValues,
  minMax,
  step = 1
}) {
  const chartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragIndexRef = useRef(-1);
  const dataRef = useRef([]);
  const dispatchTimerRef = useRef(null);
  const [chartKey, setChartKey] = useState(0);
  const [openResetConfirm, setOpenResetConfirm] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  const isMobile = windowWidth < 900;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getMinMax = () => {
    if (isExplanation && presetValues?.length) {
      const min = Math.min(...presetValues);
      const max = Math.max(...presetValues);
      const buffer = Math.ceil(max * 0.1);
      return {
        min: Math.floor(min * 0.9),
        max: Math.ceil((max + buffer) / 10) * 10
      };
    }
    if (minMax) return minMax;
    return { min: 0, max: 1000 };
  };

  const { min: rawMin, max: rawMax } = getMinMax();
  const stepNum = Math.max(1, Math.floor(step));
  const minY = Math.floor(rawMin / stepNum) * stepNum;
  const maxY = Math.ceil(rawMax / stepNum) * stepNum;
  const randomMin = Math.max(1, minY + 1);

  const updateIndicator = (chart, index, val) => {
    chart.setOption({
      series: [
        {
          markPoint: {
            data: [
              {
                coord: [index, val],
                symbol: "circle",
                symbolSize: 24,
                itemStyle: {
                  color: "#4CAF50",
                  borderColor: "#fff",
                  borderWidth: 3,
                  shadowBlur: 13,
                },
                label: {
                  show: true,
                  formatter: "⇅",
                  fontSize: 15,
                  fontWeight: "bold",
                  color: "#fff",
                },
              },
            ],
          },
          animation: false,
          animationDurationUpdate: 0,
          animationEasingUpdate: "linear",
        },
      ],
    });
  };

  const hideIndicator = (chart) => {
    chart.setOption({ series: [{ markPoint: { data: [] } }] });
  };

  const latestPropsRef = useRef({
    dispatch,
    questionState,
  });

  useEffect(() => {
    latestPropsRef.current = { dispatch, questionState };
  }, [dispatch, questionState]);

  useEffect(() => {
    const dom = chartRef.current;
    if (!dom || items.length === 0) return;

    dom.style.touchAction = "none";

    const formatValue = (val) => {
      if (maxY >= 1_000_000) {
        const v = val / 1_000_000;
        const fixed = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
        return `${fixed} jt`;
      }
      if (maxY >= 1_000) return new Intl.NumberFormat("id-ID").format(val);
      return String(val);
    };

    const len = items.length;

    const randomValues = items.map(() => {
      if (maxY <= minY) return minY;
      const stepsCount = Math.floor((maxY - minY) / stepNum);
      if (stepsCount < 1) return minY;
      const k = 1 + Math.floor(Math.random() * stepsCount);
      return minY + k * stepNum;
    });

    const clampY = (n) => {
      const stepNumVal = Number(step);
      const clamped = Math.max(minY, Math.min(maxY, n));
      if (!Number.isFinite(stepNumVal) || stepNumVal <= 1) return Math.round(clamped);
      const snapped = minY + Math.round((clamped - minY) / stepNumVal) * stepNumVal;
      return Math.max(minY, Math.min(maxY, snapped));
    };

    const normalizeToLen = (arr, fallback) => {
      const out = new Array(len);
      for (let i = 0; i < len; i++) {
        const raw = arr?.[i];
        if (raw === null || raw === undefined || String(raw).trim() === "") {
          out[i] = fallback[i];
          continue;
        }
        const num = typeof raw === "number" ? raw : Number(raw);
        out[i] = Number.isFinite(num) ? clampY(num) : fallback[i];
      }
      return out;
    };

    const answerRaw = questionState?.answerText;
    const hasUserAnswer =
      Array.isArray(answerRaw) &&
      answerRaw.length > 0 &&
      answerRaw.some((v) => v !== null && v !== undefined && String(v).trim() !== "");

    const userValues = hasUserAnswer ? normalizeToLen(answerRaw, randomValues) : null;

    const hasPreset = Array.isArray(presetValues) && presetValues.length > 0;
    const presetNormalized = hasPreset ? normalizeToLen(presetValues, randomValues) : null;

    const effectiveValues = isExplanation
      ? (presetNormalized ?? userValues ?? randomValues)
      : (userValues ?? randomValues);

    dataRef.current = effectiveValues;

    const chart = echarts.init(dom);

    chart.setOption({
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `${p.name}<br/>Nilai: ${formatValue(p.value)}`;
        },
      },
      grid: {
        left: "8%",
        right: "4%",
        bottom: isMobile ? "22%" : "14%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: items,
        axisLabel: {
          interval: 0,
          rotate: 45,
          fontSize: isMobile ? 12 : 14,
        },
      },
      yAxis: {
        type: "value",
        min: minY,
        max: maxY,
        axisLabel: { formatter: (value) => formatValue(value) },
      },
      series: [
        {
          type: "bar",
          data: effectiveValues,
          itemStyle: {
            color: "#4CAF50",
            borderRadius: [6, 6, 0, 0],
          },
          label: {
            show: true,
            position: "top",
            distance: 20,
            formatter: (params) => formatValue(params.value),
            fontSize: isMobile ? 11 : 13,
            fontWeight: "bold",
            color: "#333",
          },
          markPoint: { data: [] },
        },
      ],
    });

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    if (isExplanation) {
      dom.style.cursor = "default";
      return () => {
        if (dispatchTimerRef.current) clearTimeout(dispatchTimerRef.current);
        window.removeEventListener("resize", handleResize);
        chart.dispose();
      };
    }

    dom.style.cursor = "grab";

    let activePointerId = null;

    const getRelativePos = (evt) => {
      const rect = dom.getBoundingClientRect();
      return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    };

    const isInsideBarXY = (x, y, index, value) => {
      const top = chart.convertToPixel("grid", [index, value]);
      const bottom = chart.convertToPixel("grid", [index, minY]);
      if (!top || !bottom) return false;
      const barWidth = 40;
      const inX = Math.abs(x - top[0]) < barWidth / 2;
      const inY = y >= top[1] && y <= bottom[1];
      return inX && inY;
    };

    const endDrag = (evt) => {
      if (!isDraggingRef.current) return;
      if (evt && activePointerId !== null && evt.pointerId !== activePointerId) return;

      isDraggingRef.current = false;
      dragIndexRef.current = -1;

      hideIndicator(chart);
      dom.style.cursor = "grab";

      if (activePointerId !== null) {
        try { dom.releasePointerCapture(activePointerId); } catch { }
        activePointerId = null;
      }

      if (dispatchTimerRef.current) {
        clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }

      const cur = [...dataRef.current];
      const { dispatch: d, questionState: qs } = latestPropsRef.current;

      if (d && qs) {
        d({
          type: "question/setQuestion",
          payload: { ...qs, answerText: cur.map(String) },
        });
      }
    };

    const handlePointerDown = (evt) => {
      if (evt.pointerType === "mouse" && evt.button !== 0) return;
      if (activePointerId !== null) return;

      const { x, y } = getRelativePos(evt);
      const point = chart.convertFromPixel("grid", [x, y]);
      if (!point) return;

      const idx = Math.round(point[0]);
      if (idx < 0 || idx >= dataRef.current.length) return;

      if (!isInsideBarXY(x, y, idx, dataRef.current[idx])) return;

      evt.preventDefault();

      activePointerId = evt.pointerId;
      try { dom.setPointerCapture(evt.pointerId); } catch { }

      isDraggingRef.current = true;
      dragIndexRef.current = idx;
      dom.style.cursor = "grabbing";

      updateIndicator(chart, idx, dataRef.current[idx]);
    };

    const handlePointerMove = (evt) => {
      if (!isDraggingRef.current) return;
      if (activePointerId !== null && evt.pointerId !== activePointerId) return;

      evt.preventDefault();

      const { x, y } = getRelativePos(evt);
      const point = chart.convertFromPixel("grid", [x, y]);
      if (!point) return;

      const updated = [...dataRef.current];
      updated[dragIndexRef.current] = clampY(point[1]);
      dataRef.current = updated;

      chart.setOption({ series: [{ data: updated }] });
      updateIndicator(chart, dragIndexRef.current, updated[dragIndexRef.current]);
    };

    const handlePointerUp = (evt) => endDrag(evt);
    const handlePointerCancel = (evt) => endDrag(evt);

    dom.addEventListener("pointerdown", handlePointerDown, { passive: false });
    dom.addEventListener("pointermove", handlePointerMove, { passive: false });
    dom.addEventListener("pointerup", handlePointerUp, { passive: false });
    dom.addEventListener("pointercancel", handlePointerCancel, { passive: false });

    return () => {
      dom.removeEventListener("pointerdown", handlePointerDown);
      dom.removeEventListener("pointermove", handlePointerMove);
      dom.removeEventListener("pointerup", handlePointerUp);
      dom.removeEventListener("pointercancel", handlePointerCancel);

      window.removeEventListener("resize", handleResize);
      if (dispatchTimerRef.current) clearTimeout(dispatchTimerRef.current);
      chart.dispose();
    };
  }, [isMobile, minY, maxY, chartKey]);

  const handleResetClick = () => {
    questionState.answerText !== "" ? setOpenResetConfirm(true) : setChartKey((prev) => prev + 1);
  };

  const handleResetConfirm = () => {
    if (dispatch && questionState) {
      dispatch({
        type: "question/setQuestion",
        payload: {
          ...questionState,
          answerText: "",
        },
      });
    }
    setChartKey((prev) => prev + 1);
    setOpenResetConfirm(false);
  };

  const handleResetCancel = () => setOpenResetConfirm(false);

  return (
    <div className="interactive" id="barchart-wrapper">
      <div className="chart-col" id="barchart-container">
        <div
          id="barchart"
          ref={chartRef}
          style={{ width: "100%", minHeight: 400, paddingRight: 10 }}
        />
        {!isExplanation && (
          <>
            <button
              onClick={handleResetClick}
              style={{
                marginBottom: 16,
                padding: "8px 16px",
                backgroundColor: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 500,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#1565c0"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#1976d2"; }}
            >
              Reset
            </button>

            {openResetConfirm && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1300,
                }}
                onClick={handleResetCancel}
              >
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "4px",
                    boxShadow: "0 5px 25px rgba(0, 0, 0, 0.3)",
                    minWidth: 300,
                    maxWidth: 500,
                    zIndex: 1301,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ padding: "20px 24px", borderBottom: "1px solid #e0e0e0" }}>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>Reset jawaban?</h2>
                  </div>
                  <div style={{ padding: "20px 24px" }}>
                    <p style={{ margin: 0, color: "#666" }}>
                      Kalau kamu klik <b>Iya</b>, semua nilai bar yang sudah kamu atur akan diacak.
                    </p>
                  </div>
                  <div style={{ padding: "12px 16px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e0e0e0" }}>
                    <button
                      onClick={handleResetCancel}
                      style={{
                        padding: "6px 16px",
                        backgroundColor: "#f5f5f5",
                        color: "#333",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e0e0e0"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f5f5f5"; }}
                    >
                      Batal
                    </button>
                    <button
                      onClick={handleResetConfirm}
                      style={{
                        padding: "6px 16px",
                        backgroundColor: "#d32f2f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#c62828"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#d32f2f"; }}
                    >
                      Iya, reset
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}